import { httpClient } from './http';

export interface Waiter {
  id: string;
  fullName: string;
  email: string;
  phone?: string;
  status: 'active' | 'inactive' | 'suspended' | 'pending';
  hireDate?: string;
  salary?: number;
  notes?: string;
  restaurantId?: string;
  restaurantName?: string;
  assignedTablesCount?: number;
  totalOrders?: number;
  createdAt: string;
  updatedAt: string;
  registrationStatus?: 'pending' | 'registered';
}

export interface CreateWaiterData {
  email: string;
}

export interface UpdateWaiterData {
  full_name?: string;
  email?: string;
  phone?: string;
  status?: 'active' | 'inactive' | 'suspended';
  hire_date?: string;
  salary?: number;
  notes?: string;
}

export interface WaiterStats {
  waiterId: string;
  restaurantId: string;
  period: string;
  orderStats: {
    totalOrders: number;
    totalRevenue: number;
    averageOrderValue: number;
    completedOrders: number;
    pendingOrders: number;
  };
  tableStats: {
    totalAssignments: number;
    occupiedTables: number;
    currentAssignments: number;
  };
}

export interface WaiterAssignment {
  waiter_id: string;
  table_id: string;
  day: string;
  is_occupied?: boolean;
}

export interface WaiterListResponse {
  data: Waiter[];
  pagination: {
    page: number;
    size: number;
    total: number;
    pages: number;
  };
}

export type ShiftStatus = 'active' | 'inactive';

class WaitersService {
  async getWaiters(
    userId: string,
    restaurantId: string,
    page: number = 1,
    size: number = 20,
    status?: string,
    search?: string
  ): Promise<WaiterListResponse> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
      });

      if (status) params.append('status', status);
      if (search) params.append('search', search);

      const response = await httpClient.get<Waiter[]>(
        `/system/users/${userId}/restaurants/${restaurantId}/waiters?${params.toString()}`,
        undefined,
        'authenticated'
      );
      
      // Convertir la respuesta directa del array a la estructura esperada
      return {
        data: response,
        pagination: {
          page,
          size,
          total: response.length,
          pages: Math.ceil(response.length / size)
        }
      };
    } catch (error) {
      console.error('Error fetching waiters:', error);
      throw new Error('Error al obtener meseros');
    }
  }

  async getWaiterById(userId: string, restaurantId: string, waiterId: string): Promise<Waiter> {
    try {
      const response = await httpClient.get<Waiter>(
        `/system/users/${userId}/restaurants/${restaurantId}/waiters/${waiterId}`,
        undefined,
        'authenticated'
      );
      return response;
    } catch (error) {
      console.error('Error fetching waiter:', error);
      throw new Error('Error al obtener mesero');
    }
  }

  async createWaiter(userId: string, restaurantId: string, waiterData: CreateWaiterData): Promise<Waiter> {
    try {
      const response = await httpClient.post<Waiter>(
        `/system/users/${userId}/restaurants/${restaurantId}/waiters`,
        waiterData,
        'authenticated'
      );
      return response;
    } catch (error) {
      console.error('Error creating waiter:', error);
      throw new Error('Error al crear mesero');
    }
  }

  async updateWaiter(
    userId: string,
    restaurantId: string,
    waiterId: string,
    waiterData: UpdateWaiterData
  ): Promise<Waiter> {
    try {
      const response = await httpClient.put<Waiter>(
        `/system/users/${userId}/restaurants/${restaurantId}/waiters/${waiterId}`,
        waiterData,
        'authenticated'
      );
      return response;
    } catch (error) {
      console.error('Error updating waiter:', error);
      throw new Error('Error al actualizar mesero');
    }
  }

  async deleteWaiter(userId: string, restaurantId: string, waiterId: string): Promise<Waiter> {
    try {
      const response = await httpClient.delete<Waiter>(
        `/system/users/${userId}/restaurants/${restaurantId}/waiters/${waiterId}`,
        'authenticated'
      );
      return response;
    } catch (error) {
      console.error('Error deleting waiter:', error);
      throw new Error('Error al eliminar mesero');
    }
  }

  async assignTableToWaiter(
    userId: string,
    restaurantId: string,
    assignmentData: WaiterAssignment
  ): Promise<any> {
    try {
      const response = await httpClient.post(
        `/system/users/${userId}/restaurants/${restaurantId}/waiters/assign-table`,
        assignmentData,
        'authenticated'
      );
      return response;
    } catch (error) {
      console.error('Error assigning table to waiter:', error);
      throw new Error('Error al asignar mesa al mesero');
    }
  }

  async getWaiterStats(
    userId: string,
    restaurantId: string,
    waiterId: string,
    period: string = 'today'
  ): Promise<WaiterStats> {
    try {
      const response = await httpClient.get<WaiterStats>(
        `/system/users/${userId}/restaurants/${restaurantId}/waiters/${waiterId}/stats?period=${period}`,
        undefined,
        'authenticated'
      );
      return response;
    } catch (error) {
      console.error('Error fetching waiter stats:', error);
      throw new Error('Error al obtener estadísticas del mesero');
    }
  }

  async getWaitersByRestaurant(
    userId: string,
    restaurantId: string
  ): Promise<{ data: Waiter[]; total: number }> {
    try {
      const response = await httpClient.get<Waiter[]>(
        `/system/users/${userId}/restaurants/${restaurantId}/waiters`,
        undefined,
        'authenticated'
      );
      
      return {
        data: response,
        total: response.length
      };
    } catch (error) {
      console.error('Error fetching waiters by restaurant:', error);
      throw new Error('Error al obtener meseros del restaurante');
    }
  }

  async updateShiftStatus(
    userId: string,
    restaurantId: string,
    status: ShiftStatus
  ): Promise<{ status: ShiftStatus }>{
    try {
      const response = await httpClient.put<{ status: ShiftStatus }>(
        `/system/users/${userId}/restaurants/${restaurantId}/waiters/shift-status`,
        { status },
        'authenticated'
      );
      return response;
    } catch (error) {
      console.error('Error updating shift status:', error);
      throw new Error('No se pudo actualizar el estado de turno');
    }
  }
}

export const waitersService = new WaitersService(); 